import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, User, ChevronDown } from 'lucide-react';
import nlgLogo from '../assets/images/nlg_logo.png';
import apiService from '../services/nextlevelgames-api';
import { useUser } from '../context/UserContext';

const UserAccountModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { login } = useUser();
  const [showPhoneForm, setShowPhoneForm] = useState(false);
  const [showCodeForm, setShowCodeForm] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('+1');
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [attemptCount, setAttemptCount] = useState(0);
  const MAX_ATTEMPTS = 3;

  const handleSignIn = () => {
    setShowPhoneForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const phoneRegex = /^\d{10}$/;
    const cleanPhone = phoneNumber.replace(/\D/g, '');
    
    if (!phoneRegex.test(cleanPhone)) {
      setError('Enter a valid phone number');
      return;
    }
    
    setError('');
    setIsLoading(true);
    
    try {
      const fullPhoneNumber = countryCode + cleanPhone;
      const response = await apiService.sendVerificationCode(fullPhoneNumber);
      
      if (response.status === 'pending') {
        setShowPhoneForm(false);
        setShowCodeForm(true);
        setAttemptCount(0);
      } else {
        setError('Failed to send verification code. Please try again.');
      }
    } catch (err) {
      setError('Failed to send verification code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    
    if (verificationCode.length !== 6) {
      setError('Enter a valid 6-digit code');
      return;
    }
    
    setError('');
    setIsLoading(true);
    
    try {
      const fullPhoneNumber = countryCode + phoneNumber.replace(/\D/g, '');
      const response = await apiService.checkVerificationCode(fullPhoneNumber, verificationCode);
      
      if (response.status === 'approved' && response.verified) {
        // Verification successful - resolve customer and log in
        await resolveAndLoginCustomer(fullPhoneNumber);
      } else {
        // Verification failed
        const newAttemptCount = attemptCount + 1;
        setAttemptCount(newAttemptCount);
        
        if (newAttemptCount >= MAX_ATTEMPTS) {
          setError(`Maximum attempts reached. Please re-enter your phone number.`);
          setTimeout(() => {
            setShowCodeForm(false);
            setShowPhoneForm(true);
            setVerificationCode('');
            setAttemptCount(0);
            setError('');
          }, 2000);
        } else {
          setError(`Invalid code. ${MAX_ATTEMPTS - newAttemptCount} attempt${MAX_ATTEMPTS - newAttemptCount !== 1 ? 's' : ''} remaining.`);
          setVerificationCode('');
        }
      }
    } catch (err) {
      const newAttemptCount = attemptCount + 1;
      setAttemptCount(newAttemptCount);
      
      if (newAttemptCount >= MAX_ATTEMPTS) {
        setError(`Maximum attempts reached. Please re-enter your phone number.`);
        setTimeout(() => {
          setShowCodeForm(false);
          setShowPhoneForm(true);
          setVerificationCode('');
          setAttemptCount(0);
          setError('');
        }, 2000);
      } else {
        setError(`Invalid code. ${MAX_ATTEMPTS - newAttemptCount} attempt${MAX_ATTEMPTS - newAttemptCount !== 1 ? 's' : ''} remaining.`);
        setVerificationCode('');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const resolveAndLoginCustomer = async (fullPhoneNumber) => {
    try {
      const searchResponse = await apiService.searchCustomersByPhone(fullPhoneNumber);
      const customers = searchResponse.customers || [];

      if (customers.length === 0) {
        setError('No account found for this phone number.');
        return;
      }

      let selectedCustomer = null;
      let selectedLoyalty = null;

      if (customers.length === 1) {
        selectedCustomer = customers[0];
        // Attempt to fetch loyalty account for the single customer
        try {
          const loyaltyResponse = await apiService.searchLoyaltyAccount(selectedCustomer.id);
          if (loyaltyResponse.loyalty_accounts?.length > 0) {
            selectedLoyalty = loyaltyResponse.loyalty_accounts[0];
          }
        } catch (loyaltyErr) {
        }
      } else {
        // Multiple customers - find the one with a loyalty account
        for (const customer of customers) {
          try {
            const loyaltyResponse = await apiService.searchLoyaltyAccount(customer.id);
            if (loyaltyResponse.loyalty_accounts?.length > 0) {
              selectedCustomer = customer;
              selectedLoyalty = loyaltyResponse.loyalty_accounts[0];
              break;
            }
          } catch (loyaltyErr) {
            }
        }
        // If none had a loyalty account, default to first customer
        if (!selectedCustomer) {
          selectedCustomer = customers[0];
        }
      }

      login(selectedCustomer, selectedLoyalty);
      handleClose();
      navigate('/account');
    } catch (err) {
      setError('Failed to load account information. Please try again.');
    }
  };

  const handleClose = () => {
    setShowPhoneForm(false);
    setShowCodeForm(false);
    setPhoneNumber('');
    setVerificationCode('');
    setError('');
    setAttemptCount(0);
    onClose();
  };

  const handleBackToPhone = () => {
    setShowCodeForm(false);
    setShowPhoneForm(true);
    setVerificationCode('');
    setError('');
    setAttemptCount(0);
  };

  return ReactDOM.createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Full-screen wrapper for centering */}
          <div
            onClick={handleClose}
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}
          >
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)' }}
            />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: '28rem', margin: '0 1rem' }}
              className="bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto"
            >
            {/* Close Button */}
            <button
              onClick={handleClose}
              className="absolute top-4 left-4 p-2 rounded-lg hover:bg-gray-100 transition-colors z-10"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>

            {/* Logo */}
            <div className="flex justify-center pt-8 pb-6">
              <img src={nlgLogo} alt="Next Level Games" className="w-24 h-24 object-contain" />
            </div>

            {/* Content */}
            <div className="px-8 pb-8">
              {!showPhoneForm && !showCodeForm ? (
                <>
                  {/* User Icon */}
                  <div className="flex justify-center mb-6">
                    <User className="w-12 h-12 text-gray-400" />
                  </div>

                  {/* Title */}
                  <h2 className="text-2xl font-bold text-center mb-6">
                    Sign in or create an account with just your phone number
                  </h2>

                  {/* Benefits List */}
                  <div className="space-y-4 mb-8">
                    <div className="flex items-start space-x-3 pb-4 border-b border-gray-200">
                      <div className="flex-shrink-0 w-6 h-6 rounded-full border-2 border-gray-400 flex items-center justify-center mt-0.5">
                        <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                      </div>
                      <p className="text-gray-700">Get order status notifications</p>
                    </div>

                    <div className="flex items-start space-x-3 pb-4 border-b border-gray-200">
                      <div className="flex-shrink-0 w-6 h-6 rounded-full border-2 border-gray-400 flex items-center justify-center mt-0.5">
                        <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                      </div>
                      <p className="text-gray-700">View your order history and quickly reorder</p>
                    </div>

                    <div className="flex items-start space-x-3 pb-4 border-b border-gray-200">
                      <div className="flex-shrink-0 w-6 h-6 rounded-full border-2 border-gray-400 flex items-center justify-center mt-0.5">
                        <svg className="w-4 h-4 text-gray-400" viewBox="0 0 24 24" fill="none">
                          <rect x="3" y="6" width="18" height="12" rx="2" stroke="currentColor" strokeWidth="2"/>
                          <path d="M3 10h18" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                      </div>
                      <p className="text-gray-700">
                        Manage your Square Pay payment details for faster checkout from any Square Online site
                      </p>
                    </div>
                  </div>

                  {/* Sign In Button */}
                  <button
                    onClick={handleSignIn}
                    className="w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                  >
                    Sign in or sign up
                  </button>
                </>
              ) : showCodeForm ? (
                <>
                  {/* Verification Code Form */}
                  <form onSubmit={handleVerifyCode}>
                    <div className="mb-6">
                      <h2 className="text-xl font-bold mb-2">Enter verification code</h2>
                      <p className="text-gray-600 text-sm mb-4">
                        We sent a code to {countryCode} {phoneNumber}
                      </p>

                      {/* Code Input */}
                      <input
                        type="text"
                        value={verificationCode}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                          setVerificationCode(value);
                          setError('');
                        }}
                        placeholder="Enter 6-digit code"
                        maxLength="6"
                        className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-center text-2xl tracking-widest ${
                          error ? 'border-red-500' : 'border-gray-300'
                        }`}
                        autoFocus
                      />

                      {/* Error Message */}
                      {error && (
                        <div className="flex items-center gap-1 text-red-600 text-sm mt-2">
                          <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          {error}
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="space-y-3">
                      <button
                        type="submit"
                        disabled={isLoading || verificationCode.length !== 6}
                        className="w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isLoading ? 'Verifying...' : 'Verify'}
                      </button>
                      <button
                        type="button"
                        onClick={handleBackToPhone}
                        disabled={isLoading}
                        className="w-full px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Change phone number
                      </button>
                    </div>
                  </form>
                </>
              ) : (
                <>
                  {/* Phone Number Form */}
                  <form onSubmit={handleSubmit}>
                    <div className="mb-6">
                      <h2 className="text-xl font-bold mb-2">Enter your phone number</h2>
                      <p className="text-gray-600 text-sm mb-4">We will text you a confirmation code.</p>

                      {/* Phone Input */}
                      <div className="flex gap-2 mb-2">
                        {/* Country Code Selector */}
                        <div className="relative">
                          <select
                            value={countryCode}
                            onChange={(e) => setCountryCode(e.target.value)}
                            className="appearance-none w-24 px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white cursor-pointer"
                          >
                            <option value="+1">+1 (US)</option>
                            <option value="+44">+44 (UK)</option>
                            <option value="+91">+91 (IN)</option>
                          </select>
                          <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        </div>

                        {/* Phone Number Input */}
                        <input
                          type="tel"
                          value={phoneNumber}
                          onChange={(e) => {
                            setPhoneNumber(e.target.value);
                            setError('');
                          }}
                          placeholder="Enter phone number"
                          className={`flex-1 px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                            error ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                      </div>

                      {/* Error Message */}
                      {error && (
                        <div className="flex items-center gap-1 text-red-600 text-sm mt-2">
                          <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          {error}
                        </div>
                      )}
                    </div>

                    {/* Square Pay Logo */}
                    <div className="mb-6 flex items-center gap-2">
                      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                        <rect x="3" y="3" width="18" height="18" rx="2" />
                      </svg>
                      <span className="font-semibold">Square Pay</span>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3">
                      <button
                        type="submit"
                        disabled={isLoading}
                        className="flex-1 bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isLoading ? 'Sending...' : 'Next'}
                      </button>
                      <button
                        type="button"
                        onClick={handleClose}
                        disabled={isLoading}
                        className="px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </>
              )}
            </div>
          </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default UserAccountModal;
